
// This file is automatically generated. Do not edit it directly.
import { createClient, SupabaseClient } from '@supabase/supabase-js';
import type { Database } from './types';

const SUPABASE_URL = "https://ekyfptmpcljxlnglpdte.supabase.co";
const SUPABASE_PUBLISHABLE_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImVreWZwdG1wY2xqeGxuZ2xwZHRlIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDQzNDYwODAsImV4cCI6MjA1OTkyMjA4MH0.ZypUgq3Z2hHgGspEAmU5nh0wJPPHSHkCL6ePQVXTLpQ";

// Import the supabase client like this:
// import { supabase } from "@/integrations/supabase/client";

export const supabase = createClient<Database>(
  SUPABASE_URL,
  SUPABASE_PUBLISHABLE_KEY,
  {
    auth: {
      storage: localStorage,
      persistSession: true,
      autoRefreshToken: true,
      // Bypass navigator.locks which can deadlock on page refresh,
      // causing getSession() and all queries to hang forever
      lock: async (name: string, acquireTimeout: number, fn: () => Promise<any>) => {
        return await fn();
      },
    }
  }
);

// Helper functions to ensure buckets exist
export const ensureClosetFramesBucket = async () => {
  try {
    return await ensureBucketExists('closet-frames');
  } catch (error) {
    console.error("Error ensuring closet-frames bucket exists:", error);
    return false;
  }
};

export const ensureAvatarsBucket = async () => {
  try {
    return await ensureBucketExists('avatars');
  } catch (error) {
    console.error("Error ensuring avatars bucket exists:", error);
    return false;
  }
};

export const ensureClothingImagesBucket = async () => {
  try {
    return await ensureBucketExists('clothing-images');
  } catch (error) {
    console.error("Error ensuring clothing-images bucket exists:", error);
    return false;
  }
};

// Helper to check if videos bucket exists - doesn't attempt to create it
export const ensureVideosBucket = async () => {
  try {
    // First check if user is logged in - this is required for bucket operations
    const { data: session } = await supabase.auth.getSession();
    if (!session.session) {
      console.error("User must be logged in to check storage buckets");
      return false;
    }
    
    // Check if the bucket exists
    const { data: buckets, error: listError } = await supabase.storage.listBuckets();
    
    if (listError) {
      console.error("Error listing buckets:", listError);
      return false;
    }
    
    const videoBucketExists = buckets?.some(bucket => bucket.name === 'videos');
    
    if (!videoBucketExists) {
      // Bucket doesn't exist - log a helpful message
      console.info("Videos bucket does not exist yet. Please create it in the Supabase dashboard.");
      return false;
    }
    
    return true;
  } catch (error) {
    console.error("Error checking if videos bucket exists:", error);
    return false;
  }
};

// Generic helper to check if a bucket exists
async function ensureBucketExists(bucketName: string): Promise<boolean> {
  try {
    // Check if the bucket exists
    const { data: buckets, error } = await supabase.storage.listBuckets();
    
    if (error) {
      console.error(`Error checking ${bucketName} bucket:`, error);
      return false;
    }
    
    const bucketExists = buckets?.some(bucket => bucket.name === bucketName);
    
    if (!bucketExists) {
      // If bucket doesn't exist, don't try to create it automatically
      // This is to avoid row-level security policy violations
      console.info(`${bucketName} bucket does not exist. Please create it in the Supabase dashboard.`);
      return false;
    }
    
    return bucketExists;
  } catch (error) {
    console.error(`Error in ensureBucketExists for ${bucketName}:`, error);
    return false;
  }
}
